<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">{{ group.dict_name || '字典管理' }}</h2>
      </div>
      <div class="page-header-extra">
        <span>选项 <b>{{ list.length }}</b> 个</span>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="选项">
            <el-input v-model="query.keyword" placeholder="显示名或选项值" clearable style="width: 180px" />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 130px">
              <el-option label="启用" :value="1" />
              <el-option label="停用" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button v-if="isSuper" type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增选项</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="label" label="显示名" :min-width="isLongLabel ? 360 : 180" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ isLongLabel ? stripHtml(scope.row.label) : scope.row.label }}
          </template>
        </el-table-column>
        <el-table-column prop="value" label="选项值" min-width="180">
          <template slot-scope="scope">
            <code>{{ scope.row.value }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="80" align="center" />
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini" effect="plain">
              {{ scope.row.status === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="最近更新" width="160" />
        <el-table-column v-if="isSuper" label="操作" width="160" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="mini" @click="handleToggle(scope.row)">
              {{ scope.row.status === 1 ? '停用' : '启用' }}
            </el-button>
            <!-- 固定项分组条目写死（程序引用），不给删，停用即可 -->
            <el-button v-if="!isFixedGroup" type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

      <!-- 富文本分组编辑器工具栏长，弹窗拉宽防按钮挤压换行 -->
      <el-dialog
      :title="form._id ? '编辑选项' : '新增选项'"
      :visible.sync="dialogVisible"
      :width="isLongLabel ? '860px' : '640px'"
      top="10vh"
      class="form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="form" :model="form" :rules="formRules" label-width="110px" label-position="right">
        <el-form-item label="选项值" prop="value">
          <!-- 固定项分组：只能从程序约定的标识里选（建过的置灰），不能自由输 -->
          <el-select v-if="isFixedGroup && !form._id" v-model="form.value" placeholder="选择固定项" style="width: 100%">
            <el-option label="权益说明" value="description" :disabled="valueTaken('description')" />
            <el-option label="使用须知" value="notice" :disabled="valueTaken('notice')" />
          </el-select>
          <el-input
            v-else
            v-model="form.value"
            :disabled="!!form._id"
            :placeholder="valueIsCode ? '如 surname' : '如 餐饮'"
          />
          <span class="form-tip">保存后不建议修改，已有数据会引用该值</span>
        </el-form-item>
        <el-form-item label="显示名" prop="label">
          <!-- 长文本分组（如权益券文案）label 存富文本正文；普通分组存短选项名 -->
          <wechat-editor v-if="isLongLabel" v-model="form.label" :min-height="200" placeholder="正文内容；「使用须知」用列表，一条一项" />
          <el-input v-else v-model="form.label" placeholder="如 姓氏文化" maxlength="32" show-word-limit />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" :max="999" controls-position="right" />
          <span class="form-tip">数字越小越靠前</span>
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDictGroups, getDictList, createDictItem, updateDictItem, deleteDictItem, updateDictStatus } from '@/api/dict'
import WechatEditor from '@/components/WechatEditor.vue'

const emptyItem = () => ({
  _id: '',
  dict_code: '',
  label: '',
  value: '',
  sort: 10,
  status: 1,
  updated_at: ''
})

export default {
  name: 'DictManage',
  components: { WechatEditor },
  data() {
    return {
      loading: false,
      submitting: false,
      groups: [],
      list: [],
      query: { keyword: '', status: '' },
      dialogVisible: false,
      form: emptyItem()
    }
  },
  computed: {
    // 当前分类组编码取自路由末段：/dict/activity_category → activity_category
    activeCode() {
      const seg = this.$route.path.split('/').pop() || ''
      return this.groups.some(g => g.dict_code === seg) ? seg : ''
    },
    group() {
      return this.groups.find(g => g.dict_code === this.activeCode) || {}
    },
    valueIsCode() {
      return this.group.value_style !== 'text'
    },
    // 长文本分组：label 存正文（券详情权益说明/使用须知），编辑框换多行、上限 2000
    isLongLabel() {
      return this.group.dict_code === 'benefit_instruction'
    },
    // 固定项分组：条目 value 被程序引用（权益券文案 description/notice），可补建但不可删
    isFixedGroup() {
      return this.group.dict_code === 'benefit_instruction'
    },
    isSuper() {
      const user = this.$store.getters.getUserInfo || {}
      return Number(user.is_super) === 1
    },
    formRules() {
      const valueRules = this.valueIsCode
        ? [
            { required: true, message: '请输入选项值', trigger: 'blur' },
            { pattern: /^[a-z][a-z0-9_]*$/, message: '只能小写字母开头，可含数字与下划线', trigger: 'blur' }
          ]
        : [{ required: true, message: '请输入选项值', trigger: 'blur' }]
      // 富文本：HTML 非空不代表有内容，剥标签后判
      const labelRules = this.isLongLabel
        ? [{ validator: this.validateRichLabel, trigger: 'blur' }]
        : [{ required: true, message: '请输入显示名', trigger: 'blur' }]
      return {
        label: labelRules,
        value: valueRules
      }
    }
  },
  watch: {
    // 切换左侧分类菜单时清空筛选条件并重新拉取
    $route() {
      this.query = { keyword: '', status: '' }
      this.loadData()
    }
  },
  created() {
    this.loadGroups()
  },
  methods: {
    // 分组固定 8 组，先取分组才能解析路由对应的分类名
    loadGroups() {
      getDictGroups().then(res => {
        if (res.code !== 200) return
        this.groups = (res.data && res.data.list) || []
        if (this.activeCode) this.loadData()
      })
    },
    // 选项列表：筛选（显示名/值模糊、状态）交服务端
    loadData() {
      if (!this.activeCode) {
        this.list = []
        return
      }
      this.loading = true
      getDictList(this.activeCode, {
        keyword: this.query.keyword || '',
        status: (this.query.status === '' || this.query.status === null) ? '' : this.query.status
      }).then(res => {
        this.loading = false
        if (res.code !== 200) return
        this.list = (res.data && res.data.list) || []
      }).catch(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', status: '' }
      this.loadData()
    },
    handleAdd() {
      const maxSort = this.list.length ? Math.max.apply(null, this.list.map(i => i.sort || 0)) : 0
      this.form = Object.assign(emptyItem(), {
        dict_code: this.activeCode,
        sort: maxSort + 10
      })
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    // 固定项分组：该标识已建过则置灰（后端 valueExists 双保险）
    valueTaken(value) {
      return this.list.some(i => i.value === value)
    },
    // 富文本内容剥标签取纯文本（表格列展示 / 非空校验用）
    stripHtml(html) {
      return String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
    },
    validateRichLabel(rule, value, callback) {
      callback(this.stripHtml(value) ? undefined : new Error('请输入内容'))
    },
    handleEdit(row) {
      this.form = Object.assign(emptyItem(), row)
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleToggle(row) {
      const text = row.status === 1 ? '停用' : '启用'
      const nextStatus = row.status === 1 ? 0 : 1
      this.$confirm(`确认${text}选项「${row.label}」？`, '操作确认', { type: 'warning' }).then(() => {
        updateDictStatus(this.activeCode, row._id, { status: nextStatus }).then(res => {
          // 响应拦截器对业务错误只弹 error 但仍 resolve，须自判 code
          if (res.code !== 200) return
          this.$message.success(`已${text}`)
          this.loadData()
        })
      }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(`确认删除选项「${row.label}」？删除后业务表单将不再出现该选项。`, '删除确认', {
        type: 'warning',
        confirmButtonText: '仍要删除'
      }).then(() => {
        deleteDictItem(this.activeCode, row._id).then(res => {
          if (res.code !== 200) return
          this.$message.success('已删除')
          this.loadData()
        })
      }).catch(() => {})
    },
    // 提交白名单：编辑不带 value（后端也忽略，value 保存后不可改）
    buildPayload() {
      const payload = {
        label: this.form.label,
        sort: this.form.sort,
        status: this.form.status
      }
      if (!this.form._id) payload.value = this.form.value
      return payload
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitting = true
        const payload = this.buildPayload()
        const req = this.form._id
          ? updateDictItem(this.activeCode, this.form._id, payload)
          : createDictItem(this.activeCode, payload)
        req.then(res => {
          this.submitting = false
          if (res.code !== 200) return
          this.$message.success(this.form._id ? '已保存修改' : '新增成功')
          this.dialogVisible = false
          this.loadData()
        }).catch(() => {
          this.submitting = false
        })
      })
    }
  }
}
</script>
